import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { ENVIRONNEMENT, definirModeMaquette, estProduction, modeMaquette } from '../config';
import { peut, Capacite } from '../auth/permissions';
import { useDeconnexion, useOperateur, useRafraichissementPreventif } from '../auth/useSession';
import { LIBELLE_ROLE_INTERNE } from '../i18n/fr';
import { initiales } from '../lib/format';
import { classes } from '../lib/divers';
import { Bouton } from './Boutons';
import { Interrupteur } from './Champs';

/**
 * Libellé du repère d'environnement (BA_00013 : « local » ne disait rien au client).
 * Le serveur de recette tourne avec VITE_APP_ENV=local.
 */
const LIBELLE_ENVIRONNEMENT: Record<string, string> = {
  local: 'Serveur de recette',
  recette: 'Serveur de recette',
  staging: 'Serveur de recette',
  production: 'Production',
};

interface EntreeMenu {
  vers: string;
  libelle: string;
  capacite: Capacite;
  exact?: boolean;
}

const MENU: ReadonlyArray<{ groupe: string; entrees: EntreeMenu[] }> = [
  {
    groupe: 'Exploitation',
    entrees: [
      { vers: '/', libelle: 'Tableau de bord', capacite: Capacite.SUPERVISER, exact: true },
      { vers: '/supervision', libelle: 'Carte du parc', capacite: Capacite.SUPERVISER },
    ],
  },
  {
    groupe: 'Appareils',
    entrees: [
      { vers: '/stock', libelle: 'Stock', capacite: Capacite.CONSULTER_STOCK },
      { vers: '/stock/import', libelle: 'Import d’un lot', capacite: Capacite.IMPORTER_STOCK },
      { vers: '/programmation', libelle: 'Programmer une carte', capacite: Capacite.IMPORTER_STOCK },
      { vers: '/labo', libelle: 'Labo (cartes simulées)', capacite: Capacite.DIAGNOSTIQUER },
      { vers: '/assistant', libelle: 'Assistant (Claude)', capacite: Capacite.INTERROGER_ASSISTANT },
    ],
  },
  {
    groupe: 'Clients',
    entrees: [
      {
        vers: '/exploitations',
        libelle: 'Exploitations',
        capacite: Capacite.CONSULTER_EXPLOITATIONS,
      },
      { vers: '/audit', libelle: 'Journal d’audit', capacite: Capacite.CONSULTER_AUDIT },
    ],
  },
  {
    groupe: 'Commerce',
    entrees: [
      { vers: '/clients', libelle: 'Clients', capacite: Capacite.CONSULTER_CLIENTS },
      { vers: '/consommation', libelle: 'Consommation', capacite: Capacite.CONSULTER_CLIENTS },
      { vers: '/abonnements', libelle: 'Option SMS', capacite: Capacite.GERER_ABONNEMENTS },
      { vers: '/support', libelle: 'Support', capacite: Capacite.TRAITER_SAV },
    ],
  },
];

/**
 * Coquille applicative : barre latérale, barre supérieure, contenu.
 *
 * La barre supérieure porte deux signaux volontairement voyants : l'environnement et le mode
 * maquette. Confondre une démonstration sur données factices avec la production est l'erreur
 * la plus coûteuse que puisse commettre un opérateur de back-office — elle conduit à annoncer
 * à un client que son appareil va bien.
 */
export function Coquille({ children }: { children: ReactNode }): JSX.Element {
  const operateur = useOperateur();
  const deconnexion = useDeconnexion();
  const naviguer = useNavigate();
  const [deconnexionEnCours, setDeconnexionEnCours] = useState(false);

  useRafraichissementPreventif();

  const maquette = modeMaquette();

  return (
    <div className="gv-app">
      <nav className="gv-menu gv-sans-impression" aria-label="Navigation principale">
        <div className="gv-menu__marque">
          <span className="gv-menu__logo" aria-hidden="true">
            C
          </span>
          <div className="gv-pile gv-pile--serree">
            <strong>CREB Connect</strong>
            <span className="gv-corps-s gv-secondaire">Back-office</span>
          </div>
        </div>

        {MENU.map((section) => {
          const visibles = section.entrees.filter((e) => peut(operateur, e.capacite));
          if (visibles.length === 0) return null;
          return (
            <div key={section.groupe}>
              <p className="gv-menu__groupe gv-label">{section.groupe}</p>
              {visibles.map((entree) => (
                <NavLink
                  key={entree.vers}
                  to={entree.vers}
                  end={entree.exact ?? false}
                  className={({ isActive }) =>
                    classes('gv-menu__lien', isActive && 'gv-menu__lien--actif')
                  }
                >
                  {entree.libelle}
                </NavLink>
              ))}
            </div>
          );
        })}

        <div className="gv-menu__pied">
          <p>
            {operateur ? `${operateur.prenom} ${operateur.nom}` : '—'}
            <br />
            {operateur ? LIBELLE_ROLE_INTERNE[operateur.role] : ''}
          </p>
        </div>
      </nav>

      <div className="gv-principal">
        <header className="gv-barre gv-sans-impression">
          <span
            className={classes(
              'gv-badge',
              estProduction() ? 'gv-badge--rouge' : 'gv-badge--bleu',
            )}
            title="Serveur sur lequel vous travaillez. En production, ce repère passe au rouge."
          >
            {LIBELLE_ENVIRONNEMENT[ENVIRONNEMENT] ?? `Serveur ${ENVIRONNEMENT}`}
          </span>

          {maquette ? (
            <span className="gv-badge gv-badge--orange" title="Les données affichées sont factices">
              données factices
            </span>
          ) : null}

          <div className="gv-barre__espace" />

          {/* La bascule n'a de sens qu'en développement : en production, la maquette masquerait
              l'état réel du parc. */}
          {!estProduction() ? (
            <Interrupteur
              libelle="Données factices"
              actif={maquette}
              onChange={(actif) => {
                definirModeMaquette(actif);
              }}
            />
          ) : null}

          <span
            className="gv-badge"
            title={operateur ? `Connecté : ${operateur.prenom} ${operateur.nom} (${operateur.email})` : ''}
          >
            {operateur ? initiales(operateur.prenom, operateur.nom) : '—'}
          </span>

          <Bouton
            variante="discret"
            petit
            occupe={deconnexionEnCours}
            onClick={() => {
              setDeconnexionEnCours(true);
              void deconnexion().finally(() => {
                setDeconnexionEnCours(false);
                naviguer('/connexion', { replace: true });
              });
            }}
          >
            Se déconnecter
          </Bouton>
        </header>

        <main className="gv-contenu">{children}</main>
      </div>
    </div>
  );
}
